import {
  AGREEMENT_RECORD_PERMISSIONS,
  NO_AGREEMENT_RECORD_PERMISSIONS,
  toAgreementRecordPermissions,
  type AgreementRecordPermissions,
} from './agreementRecordPermissions'
import {
  BILL_RECORD_PERMISSIONS,
  NO_BILL_RECORD_PERMISSIONS,
  toBillRecordPermissions,
  type BillRecordPermissions,
} from './billRecordPermissions'
import {
  DOCUMENT_RECORD_PERMISSIONS,
  NO_DOCUMENT_RECORD_PERMISSIONS,
  toDocumentRecordPermissions,
  type DocumentRecordPermissions,
} from './documentRecordPermissions'
import {
  INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
  NO_INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
  toInsightsDashboardRecordPermissions,
  type InsightsDashboardRecordPermissions,
} from './insightsDashboardRecordPermissions'
import {
  NO_REQUEST_RECORD_PERMISSIONS,
  REQUEST_RECORD_PERMISSIONS,
  toRequestRecordPermissions,
  type RequestRecordPermissions,
} from './requestRecordPermissions'
import {
  NO_SUPPLIER_RECORD_PERMISSIONS,
  SUPPLIER_RECORD_PERMISSIONS,
  toSupplierRecordPermissions,
  type SupplierRecordPermissions,
} from './supplierRecordPermissions'
import {
  agreementObject,
  billObject,
  documentObject,
  insightsDashboardObject,
  requestObject,
  supplierObject,
  workflowObject,
} from './tupleBuilders'
import {
  NO_WORKFLOW_RECORD_PERMISSIONS,
  WORKFLOW_RECORD_PERMISSIONS,
  toWorkflowRecordPermissions,
  type WorkflowRecordPermissions,
} from './workflowRecordPermissions'
import type { FgaCheckKey } from './checks'
import type { RecordPermissionsEntity } from './permissionSetEntities'

// Indexed by every family below, so a family without a permission map is a compile error.
type RecordPermissionsByEntity = {
  supplier: SupplierRecordPermissions
  request: RequestRecordPermissions
  agreement: AgreementRecordPermissions
  bill: BillRecordPermissions
  workflow: WorkflowRecordPermissions
  document: DocumentRecordPermissions
  insights_dashboard: InsightsDashboardRecordPermissions
}

export type RecordPermissionsFor<Entity extends RecordPermissionsEntity> = RecordPermissionsByEntity[Entity]

/** How one record type's permissions are asked of the model and read back. */
type RecordPermissionsDefinition<Permissions> = {
  /** The answer for a caller with no access, and the seed every result is built from. */
  none: Permissions
  /** One check per permission for `user` on the record, in the order {@link fromChecks} reads the answers. */
  checks: (user: string, recordId: string) => FgaCheckKey[]
  /** The check answering one permission, or null when the record type reports no such permission. */
  checkFor: (permission: string) => ((user: string, recordId: string) => FgaCheckKey) | null
  fromChecks: (allowed: readonly boolean[]) => Permissions
}

// `checkOf` is written per type so each check's relation is checked against its own object type.
const recordPermissionsDefinition = <Key extends string, Relation extends string>(
  pairs: readonly (readonly [Key, Relation])[],
  none: Record<Key, boolean>,
  fromChecks: (allowed: readonly boolean[]) => Record<Key, boolean>,
  checkOf: (user: string, recordId: string, relation: Relation) => FgaCheckKey
): RecordPermissionsDefinition<Record<Key, boolean>> => {
  const relationByPermission = new Map<string, Relation>(pairs)
  return {
    none,
    checks: (user, recordId) => pairs.map(([, relation]) => checkOf(user, recordId, relation)),
    checkFor: (permission) => {
      const relation = relationByPermission.get(permission)
      return relation == null ? null : (user, recordId) => checkOf(user, recordId, relation)
    },
    fromChecks,
  }
}

/** Every record type the record-permission reads answer for, with how each is checked. */
export const RECORD_PERMISSIONS_DEFINITIONS: {
  [Entity in RecordPermissionsEntity]: RecordPermissionsDefinition<RecordPermissionsFor<Entity>>
} = {
  supplier: recordPermissionsDefinition(
    SUPPLIER_RECORD_PERMISSIONS,
    NO_SUPPLIER_RECORD_PERMISSIONS,
    toSupplierRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: supplierObject(recordId) })
  ),
  request: recordPermissionsDefinition(
    REQUEST_RECORD_PERMISSIONS,
    NO_REQUEST_RECORD_PERMISSIONS,
    toRequestRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: requestObject(recordId) })
  ),
  agreement: recordPermissionsDefinition(
    AGREEMENT_RECORD_PERMISSIONS,
    NO_AGREEMENT_RECORD_PERMISSIONS,
    toAgreementRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: agreementObject(recordId) })
  ),
  bill: recordPermissionsDefinition(
    BILL_RECORD_PERMISSIONS,
    NO_BILL_RECORD_PERMISSIONS,
    toBillRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: billObject(recordId) })
  ),
  workflow: recordPermissionsDefinition(
    WORKFLOW_RECORD_PERMISSIONS,
    NO_WORKFLOW_RECORD_PERMISSIONS,
    toWorkflowRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: workflowObject(recordId) })
  ),
  document: recordPermissionsDefinition(
    DOCUMENT_RECORD_PERMISSIONS,
    NO_DOCUMENT_RECORD_PERMISSIONS,
    toDocumentRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: documentObject(recordId) })
  ),
  insights_dashboard: recordPermissionsDefinition(
    INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
    NO_INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
    toInsightsDashboardRecordPermissions,
    (user, recordId, relation) => ({ user, relation, object: insightsDashboardObject(recordId) })
  ),
}
